import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase';
import GoogleIcon from './GoogleIcon';
import Notification from './Notification';
import './Auth.css';

const SignIn = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState(null);
    const [showResetForm, setShowResetForm] = useState(false);
    const navigate = useNavigate();

    const validateForm = () => {
        const newErrors = {};
        
        if (!email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter a valid email';
        }

        if (!password) {
            newErrors.password = 'Password is required';
        } else if (password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEmailSignIn = async (e) => {
        e.preventDefault();
        setErrors({});
        setNotification(null);

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            await signInWithEmailAndPassword(auth, email, password);
            setNotification({
                type: 'success',
                message: 'Successfully signed in!'
            });
            setTimeout(() => navigate('/dashboard'), 1000);
        } catch (error) {
            let errorMessage = 'Failed to sign in. Please check your credentials.';
            if (error.code === 'auth/user-not-found') {
                errorMessage = 'No account found with this email.';
            } else if (error.code === 'auth/wrong-password') {
                errorMessage = 'Incorrect password.';
            }
            setNotification({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setErrors({});
        setNotification(null);
        setLoading(true);

        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            setNotification({
                type: 'success',
                message: 'Successfully signed in with Google!'
            });
            setTimeout(() => navigate('/dashboard'), 1000);
        } catch (error) {
            setNotification({
                type: 'error',
                message: 'Failed to sign in with Google. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setErrors({});
        setNotification(null);

        if (!email) {
            setErrors({ email: 'Email is required' });
            return;
        }

        if (!/\S+@\S+\.\S+/.test(email)) {
            setErrors({ email: 'Please enter a valid email' });
            return;
        }

        setLoading(true);

        try {
            await sendPasswordResetEmail(auth, email);
            setNotification({
                type: 'success',
                message: 'Password reset email sent! Please check your inbox.'
            });
            setShowResetForm(false);
        } catch (error) {
            let errorMessage = 'Failed to send reset email. Please try again.';
            if (error.code === 'auth/user-not-found') {
                errorMessage = 'No account found with this email.';
            }
            setNotification({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            {notification && (
                <Notification
                    type={notification.type}
                    message={notification.message}
                    onClose={() => setNotification(null)}
                />
            )}
            <div className="auth-card">
                <div className="auth-header">
                    <h1>Welcome Back</h1>
                    <p>Sign in to continue learning</p>
                </div>

                {showResetForm ? (
                    <form onSubmit={handlePasswordReset} className="auth-form">
                        <div className="form-group">
                            <label htmlFor="reset-email">Email</label>
                            <input
                                type="email"
                                id="reset-email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={errors.email ? 'error' : ''}
                                disabled={loading}
                            />
                            {errors.email && (
                                <span className="error-message">{errors.email}</span>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="auth-button"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="loading-spinner" />
                                    <span style={{ opacity: 0 }}>Send Reset Link</span>
                                </>
                            ) : (
                                'Send Reset Link'
                            )}
                        </button>

                        <button
                            type="button"
                            className="text-button"
                            onClick={() => setShowResetForm(false)}
                            disabled={loading}
                        >
                            Back to Sign In
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleEmailSignIn} className="auth-form">
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={errors.email ? 'error' : ''}
                                disabled={loading}
                            />
                            {errors.email && (
                                <span className="error-message">{errors.email}</span>
                            )}
                        </div>

                        <div className="form-group">
                            <div className="password-header">
                                <label htmlFor="password">Password</label>
                                <button
                                    type="button"
                                    className="text-button"
                                    onClick={() => setShowResetForm(true)}
                                    disabled={loading}
                                >
                                    Forgot Password?
                                </button>
                            </div>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={errors.password ? 'error' : ''}
                                disabled={loading}
                            />
                            {errors.password && (
                                <span className="error-message">{errors.password}</span>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="auth-button"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="loading-spinner" />
                                    <span style={{ opacity: 0 }}>Sign In</span>
                                </>
                            ) : (
                                'Sign In'
                            )}
                        </button>

                        <div className="auth-divider">
                            <span>or</span>
                        </div>

                        <button
                            type="button"
                            className="google-button"
                            onClick={handleGoogleSignIn}
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="loading-spinner" />
                            ) : (
                                <>
                                    <GoogleIcon />
                                    Continue with Google
                                </>
                            )}
                        </button>
                    </form>
                )}

                <div className="auth-footer">
                    Don't have an account?{' '}
                    <Link to="/signup" style={{ pointerEvents: loading ? 'none' : 'auto' }}>
                        Sign up
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default SignIn; 